import { formatCurrency, formatDollars, stripZeros, truncateNumbers } from '@kwenta/sdk/utils'
import { wei } from '@kwenta/wei'
import { memo, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { TransactionButton } from 'components/Button/Button'
import ErrorView from 'components/ErrorView'
import { InfoBoxCol } from 'components/InfoBox'
import NumericInput from 'components/Input/NumericInput'
import Loader from 'components/Loader'
import { FlexDivCol, FlexDivRowCentered } from 'components/layout/flex'
import { DEFAULT_USDC_DECIMALS } from 'constants/defaults'
import { selectConditionalOrdersCount, selectSubmittingFuturesTx } from 'state/futures/selectors'
import { withdrawCMAccountKeeperBalance } from 'state/futures/snxPerpsV3/actions'
import { selectKeeperUSDBalance } from 'state/futures/snxPerpsV3/selectors'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { selectIsSubAccountMode } from 'state/wallet/selectors'

const WithdrawKeeperBalanceCrossMargin = memo(() => {
	const { t } = useTranslation()
	const dispatch = useAppDispatch()
	const isSubAccountMode = useAppSelector(selectIsSubAccountMode)
	const isSubmitting = useAppSelector(selectSubmittingFuturesTx)
	const keeperBalance = useAppSelector(selectKeeperUSDBalance)
	const pendingOrdersCount = useAppSelector(selectConditionalOrdersCount)

	const [amount, setAmount] = useState('')

	const maxBalanceDisplay = useMemo(
		() => stripZeros(truncateNumbers(keeperBalance, DEFAULT_USDC_DECIMALS)),
		[keeperBalance]
	)

	const onWithdrawKeeperDeposit = useCallback(() => {
		if (wei(keeperBalance).eq(0)) return

		dispatch(withdrawCMAccountKeeperBalance(wei(amount)))
		setAmount('')
	}, [dispatch, amount, keeperBalance])

	const exceedsLimit = useMemo(() => wei(amount || 0).gt(keeperBalance), [amount, keeperBalance])

	const isDisabled = useMemo(
		() => !amount || isSubmitting || exceedsLimit || isSubAccountMode,
		[amount, isSubmitting, exceedsLimit, isSubAccountMode]
	)

	const handleSetMax = useCallback(() => {
		setAmount(maxBalanceDisplay)
	}, [maxBalanceDisplay])

	return (
		<FlexDivCol rowGap="20px">
			<InfoBoxContainer>
				<InfoBoxCol
					title={t('futures.modals.manage.order.total-balance')}
					textValue={formatDollars(keeperBalance, { maxDecimals: 2 })}
				/>
				<InfoBoxCol
					title={t('futures.modals.manage.order.total-open-orders')}
					textValue={pendingOrdersCount.toString()}
				/>
			</InfoBoxContainer>
			<FlexDivCol rowGap="10px">
				<BalanceContainer onClick={handleSetMax}>
					<BalanceText>{t('futures.market.trade.margin.modal.balance')}</BalanceText>
					<BalanceText>
						<span>
							{formatCurrency('USDC', keeperBalance, {
								currencyKey: 'USDC',
								maxDecimals: 2,
								removeTrailingZeros: true,
							})}
						</span>
					</BalanceText>
				</BalanceContainer>
				<StyledNumericInput
					dataTestId="futures-market-trade-keeper-margin-input"
					placeholder={maxBalanceDisplay}
					value={truncateNumbers(amount, DEFAULT_USDC_DECIMALS)}
					invalid={exceedsLimit}
					onChange={(_, v) => {
						setAmount(v)
					}}
				/>
			</FlexDivCol>

			<TransactionButton
				data-testid="futures-market-trade-manage-keeper-button"
				disabled={isDisabled}
				fullWidth
				onClick={onWithdrawKeeperDeposit}
			>
				{isSubmitting ? (
					<Loader width="20px" height="20px" />
				) : (
					t('futures.market.trade.orders.manage-keeper-deposit.withdraw')
				)}
			</TransactionButton>
			{isSubAccountMode && (
				<ErrorView
					messageType="warn"
					message={t('futures.market.trade.confirmation.modal.disabled-keeper-bal-management')}
					containerStyle={{ margin: '16px 0 0 0' }}
				/>
			)}
		</FlexDivCol>
	)
})

export default WithdrawKeeperBalanceCrossMargin

const BalanceContainer = styled(FlexDivRowCentered)`
	cursor: pointer;
	p {
		margin: 0;
	}
`

const BalanceText = styled.p<{ $gold?: boolean }>`
	color: ${(props) =>
		props.$gold ? props.theme.colors.selectedTheme.yellow : props.theme.colors.selectedTheme.gray};
	span {
		color: ${(props) => props.theme.colors.selectedTheme.button.text.primary};
	}
`

const InfoBoxContainer = styled.div`
	box-sizing: border-box;
	width: 100%;
	border: ${(props) => props.theme.colors.selectedTheme.border};
	border-radius: 8px;
	padding: 15px;
	display: grid;
	grid-template-columns: 1fr 1fr;
	grid-gap: 15px 15px;
`

const StyledNumericInput = styled(NumericInput)`
	padding: 10px 15px;
	height: 100%;
	input {
		font-size: 15px;
		font-weight: 400;
		font-feature-settings: 'tnum';
		color: ${(props) => props.theme.colors.selectedTheme.newTheme.text.primary};
		border: none;
		background-color: transparent;

		&:focus {
			outline: none;
		}
	}
`
